/**
 * EF-CLIENT-PORTAL-UI-V1
 * Client Projects List Page
 * 
 * Features:
 * - Paginated project list from API
 * - Server-side pagination
 * - Click to view project details
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import {
  Briefcase,
  Loader2,
  ChevronRight,
  ChevronLeft,
  Search,
  Calendar,
  Users,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function ClientProjectsPage() {
  const navigate = useNavigate();
  const { getToken } = useOutletContext();
  // EVENTSFLO-PORTAL-I18N-PHASE-2B-CLIENT-PORTAL-COVERAGE-V1
  const { t } = useTranslation(["clientPortal", "common"]);
  
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [meta, setMeta] = useState({ page: 1, limit: 20, total: 0, total_pages: 0 });
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const fetchProjects = useCallback(async (page = 1, searchTerm = "") => {
    const token = getToken();
    if (!token) return;

    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: "20",
      });
      if (searchTerm) params.append("search", searchTerm);

      const response = await fetch(`${API_URL}/api/client/projects?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to fetch projects");

      const data = await response.json();
      setProjects(data.data || []);
      setMeta(data.meta || { page: 1, limit: 20, total: 0, total_pages: 0 });
    } catch (err) {
      console.error("Projects fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchProjects(1, search);
  }, [fetchProjects, search]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  const handlePageChange = (newPage) => {
    fetchProjects(newPage, search);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
      case "confirmed":
        return "bg-green-500/10 text-green-500 border-green-500/20";
      case "draft":
        return "bg-yellow-500/10 text-yellow-500 border-yellow-500/20";
      case "completed":
        return "bg-blue-500/10 text-blue-500 border-blue-500/20";
      case "cancelled":
        return "bg-red-500/10 text-red-500 border-red-500/20";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold" data-testid="projects-title">{t("clientPortal:projects.title")}</h1>
          <p className="text-sm text-muted-foreground">
            {t("clientPortal:projects.totalLabel", { count: meta.total })}
          </p>
        </div>

        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t("clientPortal:projects.searchPlaceholder")}
              className="pl-9 w-[200px]"
              data-testid="project-search-input"
            />
          </div>
          <Button type="submit" size="sm" data-testid="project-search-btn">
            {t("clientPortal:projects.searchButton")}
          </Button>
        </form>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : projects.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Briefcase className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">{t("clientPortal:projects.empty")}</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid gap-3">
            {projects.map((project) => (
              <Card
                key={project.id}
                className="cursor-pointer hover:bg-accent/50 transition-colors"
                onClick={() => navigate(`/client/projects/${project.id}`)}
                data-testid={`project-card-${project.id}`}
              >
                <CardContent className="py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      {/* EF-CLIENT-PORTAL-UI-REFINEMENT-V1: Improved header with status */}
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold truncate">{project.name}</h3>
                        <Badge variant="outline" className={getStatusColor(project.status)}>
                          {t(`clientPortal:projects.status.${(project.status || "").toLowerCase()}`, {
                            defaultValue: project.status,
                          })}
                        </Badge>
                      </div>

                      {/* Project meta info - cleaner layout */}
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                        {project.start_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {project.start_date}
                            {project.end_date && project.end_date !== project.start_date && (
                              <span className="text-muted-foreground/60"> — {project.end_date}</span>
                            )}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Briefcase className="h-3 w-3" />
                          {t("clientPortal:projects.shiftsCount", { count: project.total_shifts || 0 })}
                        </span>
                        {/* EVENTSFLO-CLIENT-PORTAL-CREW-COUNT-LAYER-AUDIT-FIX-V1 */}
                        {(project.crew_required_total || 0) > 0 && (
                          <span
                            className="flex items-center gap-1"
                            data-testid={`project-${project.id}-crew-required`}
                          >
                            <Users className="h-3 w-3" />
                            {t("clientPortal:projects.crewRequired", { count: project.crew_required_total })}
                          </span>
                        )}
                        {project.assigned_crew_count > 0 && (
                          <span
                            className="flex items-center gap-1 text-[10px] opacity-75"
                            data-testid={`project-${project.id}-assigned-crew`}
                          >
                            {t("clientPortal:projects.assignedCrew", { count: project.assigned_crew_count })}
                          </span>
                        )}
                      </div>
                      
                      {/* Next shift indicator */}
                      {project.upcoming_shifts > 0 && (
                        <div className="mt-2 text-xs">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                            <Calendar className="h-3 w-3" />
                            {t("clientPortal:projects.upcomingShiftsBadge", { count: project.upcoming_shifts })}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0 mt-1" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {meta.total_pages > 1 && (
            <div className="flex items-center justify-between pt-4">
              <p className="text-sm text-muted-foreground">
                {t("clientPortal:projects.pagination.pageOf", { page: meta.page, total: meta.total_pages })}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={meta.page <= 1}
                  onClick={() => handlePageChange(meta.page - 1)}
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  {t("clientPortal:projects.pagination.previous")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={meta.page >= meta.total_pages}
                  onClick={() => handlePageChange(meta.page + 1)}
                  data-testid="next-page-btn"
                >
                  {t("clientPortal:projects.pagination.next")}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

